// ============================================================
// TERROR ENGINE — SPRITE MANIFEST
// Every player and companion sprite URL, defined once.
// All zones import from here. Add a new frame? Add it here.
// Aug 2026 art drop (Lyraei + Lestre): real second walk frames for
// EVERY direction, both characters, plus a directional REST frame and
// a multi-frame true-idle cycle per character. Three states now, not
// two — walk, rest, idle. See PLAYER_SPRITES and IDLE_CYCLES below.
// ============================================================

import { CDN } from "./CDN";

// ── Player sprites ────────────────────────────────────────────
// THREE ANIMATION STATES, per gender:
//   walk  — player_{g}_{dir}_{a|b}      alternating while moving
//   rest  — player_{g}_rest_{dir}       the instant movement stops, direction kept
//   idle  — player_{g}_idle_{1..n}      the "true" idle, after standing still a while
// Aug 2026 (Lyraei / Lestre art drop): every direction has a real _b walk
// frame (male down excepted — one frame only) and a real directional rest
// frame, so nothing duplicates a frame to fake a cycle any more.
export const PLAYER_SPRITES = {
  // Female (Lyraei) — walk
  player_f_down_a:  `${CDN}/player_f_down_a.webp`,
  player_f_down_b:  `${CDN}/player_f_down_b.webp`,
  player_f_up_a:    `${CDN}/player_f_up_a.webp`,
  player_f_up_b:    `${CDN}/player_f_up_b.webp`,
  player_f_left_a:  `${CDN}/player_f_left_a.webp`,
  player_f_left_b:  `${CDN}/player_f_left_b.webp`,
  player_f_right_a: `${CDN}/player_f_right_a.webp`,
  player_f_right_b: `${CDN}/player_f_right_b.webp`,
  // Female — directional rest (movement just stopped, still facing that way)
  player_f_rest_down:  `${CDN}/player_f_rest_down.webp`,
  player_f_rest_up:    `${CDN}/player_f_rest_up.webp`,
  player_f_rest_left:  `${CDN}/player_f_rest_left.webp`,
  player_f_rest_right: `${CDN}/player_f_rest_right.webp`,
  // Female — true idle: 1 ↔ 2, forever
  player_f_idle_1:  `${CDN}/player_f_idle_1.webp`,
  player_f_idle_2:  `${CDN}/player_f_idle_2.webp`,
  player_f_default: `${CDN}/player_f_rest_down.webp`,

  // Male (Lestre) — walk
  player_m_down_a:  `${CDN}/player_m_down_a.webp`,
  player_m_down_b:  `${CDN}/player_m_down_b.webp`,
  player_m_up_a:    `${CDN}/player_m_up_a.webp`,
  player_m_up_b:    `${CDN}/player_m_up_b.webp`,
  player_m_left_a:  `${CDN}/player_m_left_a.webp`,
  player_m_left_b:  `${CDN}/player_m_left_b.webp`,
  player_m_right_a: `${CDN}/player_m_right_a.webp`,
  player_m_right_b: `${CDN}/player_m_right_b.webp`,
  // Male — directional rest
  player_m_rest_down:  `${CDN}/player_m_rest_down.webp`,
  player_m_rest_up:    `${CDN}/player_m_rest_up.webp`,
  player_m_rest_left:  `${CDN}/player_m_rest_left.webp`,
  player_m_rest_right: `${CDN}/player_m_rest_right.webp`,
  // Male — true idle: 1, then 2 ↔ 3 forever
  player_m_idle_1:  `${CDN}/player_m_idle_1.webp`,
  player_m_idle_2:  `${CDN}/player_m_idle_2.webp`,
  player_m_idle_3:  `${CDN}/player_m_idle_3.webp`,
  player_m_default: `${CDN}/player_m_rest_down.webp`,
};

// The true-idle frame order, per gender. Index 0 is the entry frame; the
// LOOP slice is what repeats forever after it. Female enters on 1 and
// swings 1↔2; male enters on 1 once, then swings 2↔3 and never shows 1
// again until he moves. Change an idle rhythm HERE, nowhere else.
export const IDLE_CYCLES = {
  f: { enter: ["player_f_idle_1"], loop: ["player_f_idle_2", "player_f_idle_1"] },
  m: { enter: ["player_m_idle_1"], loop: ["player_m_idle_2", "player_m_idle_3"] },
};

// ── Companions ────────────────────────────────────────────────
// Explicit overrides only. Anything NOT listed here is derived on the
// fly as `${CDN}/${companionId}.webp` (see getCompanionSprite). That
// means a brand-new companion needs ZERO entries here — drop its art
// at terror-assets/<id>.webp and it appears everywhere at once.
//
// `default` is the universal safety net: if a companion's own art file
// doesn't exist yet, the <img> onError swaps to this (J'ynxi the
// prototype), so a room never renders a broken sprite. THIS is why the
// companion looked like J'ynxi in every room before — there was no
// per-id path, so every id fell through to the default. Now each id
// gets its OWN url first; J'ynxi is only the fallback-of-last-resort.
export const COMPANION_SPRITES = {
  default: `${CDN}/jynxi.webp`,
  jynxi:   `${CDN}/jynxi.webp`,
};

// The url every un-arted companion falls back to (used by <img> onError).
export const COMPANION_FALLBACK = COMPANION_SPRITES.default;

// ── Zone backgrounds ──────────────────────────────────────────
export const ZONE_BACKGROUNDS = {
  plaza:        `${CDN}/p_center.webp`,
  workshop:     `${CDN}/p_forge.webp`,
  faction_hall: `${CDN}/p_alliance.webp`,
};

// ── Zone NPC / rep sprites ────────────────────────────────────
export const NPC_SPRITES = {
  ser_vael: `${CDN}/p_nihil.webp`,
  calyx:    `${CDN}/p_omnia.webp`,
};

// ── Helpers ───────────────────────────────────────────────────

/**
 * Resolve directional player sprite.
 * @param {string} gender  "f" | "m"
 * @param {number} facingX -1 | 0 | 1
 * @param {number} facingY -1 | 0 | 1
 * @param {boolean} walkFrame  true = _b frame, false = _a frame
 */
export function getPlayerSprite(gender, facingX, facingY, walkFrame) {
  const g     = gender === "f" ? "f" : "m";
  const frame = walkFrame ? "b" : "a";
  let dir = "down";
  if (facingY === -1)      dir = "up";
  else if (facingY === 1)  dir = "down";
  else if (facingX === -1) dir = "left";
  else if (facingX === 1)  dir = "right";
  return (
    PLAYER_SPRITES[`player_${g}_${dir}_${frame}`] ||
    PLAYER_SPRITES[`player_${g}_${dir}_a`]        ||
    PLAYER_SPRITES[`player_${g}_default`]
  );
}

/**
 * Resolve the directional REST sprite — what shows the instant the player
 * stops moving, before the true idle takes over. Keeps the facing.
 */
export function getRestSprite(gender, facingX, facingY) {
  const g = gender === "f" ? "f" : "m";
  let dir = "down";
  if (facingY === -1)      dir = "up";
  else if (facingY === 1)  dir = "down";
  else if (facingX === -1) dir = "left";
  else if (facingX === 1)  dir = "right";
  return (
    PLAYER_SPRITES[`player_${g}_rest_${dir}`] ||
    PLAYER_SPRITES[`player_${g}_default`]
  );
}

/**
 * Resolve a TRUE IDLE frame, gender-aware.
 * @param {string} gender "f" | "m"
 * @param {number} step   0,1,2,3… — how many idle frames have elapsed.
 *                        Booleans from older call sites still work
 *                        (false→0, true→1) and read as the first swing.
 * Direction is deliberately ignored: once the true idle begins, both
 * characters play the same cycle regardless of which way they were facing.
 */
export function getIdleSprite(gender, step) {
  const g = gender === "f" ? "f" : "m";
  const { enter, loop } = IDLE_CYCLES[g];
  const i = Math.max(0, Math.floor(Number(step) || 0));
  const key = i < enter.length
    ? enter[i]
    : loop[(i - enter.length) % loop.length];
  return PLAYER_SPRITES[key] || PLAYER_SPRITES[`player_${g}_default`];
}

/**
 * Companion sprite by id.
 * Priority:
 *   1. explicit override in COMPANION_SPRITES (e.g. jynxi)
 *   2. derived per-companion CDN art: `${CDN}/${id}.webp`
 *   3. (at render time) <img> onError → COMPANION_FALLBACK (J'ynxi)
 *
 * Returning the derived url for unknown ids — rather than the J'ynxi
 * default — is what lets each companion show its OWN art the moment the
 * file lands on the CDN, with no code change. Until that art exists the
 * url 404s and the renderer's onError falls back to J'ynxi gracefully.
 */
export function getCompanionSprite(companionId) {
  if (!companionId) return COMPANION_SPRITES.default;
  if (COMPANION_SPRITES[companionId]) return COMPANION_SPRITES[companionId];
  return `${CDN}/${companionId}.webp`;
}

/** The url to swap to when a companion's own art fails to load. */
export function getCompanionFallback() {
  return COMPANION_FALLBACK;
}
